import React, { useEffect, useState } from "react";
import {
  AlertCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  FileText,
  Globe,
  History,
  Loader2,
} from "lucide-react";
import api from "../services/api";
import DashboardModuleShell from "../components/DashboardModuleShell";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import alertService from "../utils/alert";
import { cn } from "../utils/cn";
import { formatCurrency } from "../utils/currency";
import { useCompanyCurrency } from "../hooks/useCompanySettings";

function formatInvoiceDate(value) {
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function InvoicesPage() {
  const companyCurrency = useCompanyCurrency();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, pages: 1 });

  useEffect(() => {
    loadInvoices();
  }, []);

  const loadInvoices = async (page = 1) => {
    try {
      setLoading(true);
      const res = await api.get("/invoices", { params: { page, limit: 10 } });
      setInvoices(res.data.invoices || []);
      setPagination(res.data.pagination || { page: 1, pages: 1 });
    } catch (err) {
      alertService.error("Error", "Failed to load invoices.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoiceId, invoiceNumber) => {
    try {
      setDownloading(invoiceId);
      // Uses the shared `api` client (same baseURL/auth handling as every
      // other request on this page) instead of a raw fetch() against a
      // hardcoded port — that port previously didn't match this dev
      // environment's actual backend port, so every download silently failed.
      const response = await api.get(`/invoices/${invoiceId}/download`, { responseType: 'blob' });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alertService.toast("success", "Invoice downloaded successfully");
    } catch (err) {
      alertService.error("Download Failed", "Failed to retrieve invoice PDF.");
    } finally {
      setDownloading(null);
    }
  };

  const getStatusVariant = (status = "") => {
    switch (status.toLowerCase()) {
      case "paid":
        return "emerald";
      case "issued":
      case "pending":
        return "amber";
      case "overdue":
      case "failed":
        return "rose";
      default:
        return "slate";
    }
  };

  const totalAmount = invoices.reduce((sum, invoice) => sum + Number(invoice.amount?.totalAmount || 0), 0);
  const unpaidCount = invoices.filter((invoice) => (invoice.status || "").toLowerCase() !== "paid").length;
  const paidCount = invoices.filter((invoice) => (invoice.status || "").toLowerCase() === "paid").length;

  const dashboardStats = [
    { label: "Total Invoices", value: invoices.length, icon: History },
    { label: "Paid", value: paidCount, icon: CreditCard },
    { label: "Pending", value: unpaidCount, icon: AlertCircle },
    { label: "Current Page", value: pagination.page, icon: Calendar, meta: `${pagination.pages} total` },
  ];

  return (
    <DashboardModuleShell
      title="Invoices"
      subtitle="Review billing history and download invoice PDFs from the finance workspace."
      icon={FileText}
      stats={dashboardStats}
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="p-6 xl:col-span-2">
          <div className="flex items-center justify-between gap-4 mb-6 pb-4 border-b border-[#D8DDE6]">
            <div>
              <h2 className="text-[16px] font-bold text-[#080707] mb-1">Invoice Ledger</h2>
              <p className="text-[12px] font-medium text-[#706E6B] mb-0">
                All invoice records synced to your billing account.
              </p>
            </div>
            <Badge variant="slate">{invoices.length} records</Badge>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#F4F6F9] border-y border-[#D8DDE6]">
                  <th className="px-4 py-2.5 text-left text-[11px] font-bold text-[#706E6B] uppercase ">Invoice #</th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-bold text-[#706E6B] uppercase ">Date</th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-bold text-[#706E6B] uppercase ">Plan</th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-bold text-[#706E6B] uppercase ">Amount</th>
                  <th className="px-4 py-2.5 text-left text-[11px] font-bold text-[#706E6B] uppercase ">Status</th>
                  <th className="px-4 py-2.5 text-right text-[11px] font-bold text-[#706E6B] uppercase ">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading && invoices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <Loader2 size={28} className="animate-spin text-[#0176D3]" />
                        <span className="text-[12px] font-bold uppercase text-[#706E6B]">
                          Loading invoices
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-14 h-14 rounded-[4px] bg-[#F4F6F9] border border-[#D8DDE6] flex items-center justify-center text-[#706E6B]">
                          <FileText size={26} />
                        </div>
                        <div>
                          <div className="text-[14px] font-bold text-[#080707]">No invoices found</div>
                          <div className="text-[12px] font-medium text-[#706E6B] mt-1">
                            New billing records will appear here after payment.
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  invoices.map((invoice) => (
                    <tr key={invoice._id} className="border-b border-[#D8DDE6] hover:bg-[#F4F6F9] transition-colors">
                      <td className="px-4 py-4 text-[13px] font-bold text-[#080707]">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-4 py-4 text-[13px] text-[#706E6B]">
                        {formatInvoiceDate(invoice.createdAt)}
                      </td>
                      <td className="px-4 py-4 text-[13px] text-[#080707]">
                        {invoice.subscriptionPlan?.name || "Subscription Plan"}
                      </td>
                      <td className="px-4 py-4 text-[13px] font-bold text-[#080707]">
                        {formatCurrency(invoice.amount?.totalAmount, invoice.amount?.currency || invoice.currency || companyCurrency)}
                      </td>
                      <td className="px-4 py-4">
                        <Badge variant={getStatusVariant(invoice.status)}>
                          {invoice.status || "unknown"}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          icon={Download}
                          loading={downloading === invoice._id}
                          className="normal-case tracking-normal"
                          onClick={() => handleDownload(invoice._id, invoice.invoiceNumber)}
                        >
                          Download
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.pages > 1 ? (
            <div className="flex items-center justify-between gap-4 pt-4 mt-4 border-t border-[#D8DDE6]">
              <div className="text-[11px] font-bold text-[#706E6B] uppercase ">
                Page {pagination.page} of {pagination.pages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  icon={ChevronLeft}
                  disabled={pagination.page === 1}
                  className="w-8 px-0"
                  onClick={() => loadInvoices(pagination.page - 1)}
                />
                {Array.from({ length: pagination.pages }, (_, index) => index + 1)
                  .slice(Math.max(0, pagination.page - 3), Math.max(5, pagination.page + 2))
                  .map((page) => (
                    <button
                      key={page}
                      onClick={() => loadInvoices(page)}
                      className={cn(
 "min-w-8 h-8 px-2 rounded border text-[13px] font-semibold transition-colors",
 pagination.page === page
 ? "bg-[#0176D3] border-[#0176D3] text-white"
 : "bg-white border-[#D8DDE6] text-[#706E6B] hover:bg-[#F4F6F9] hover:text-[#080707]"
 )}
                    >
                      {page}
                    </button>
                  ))}
                <Button
                  variant="outline"
                  size="sm"
                  icon={ChevronRight}
                  disabled={pagination.page === pagination.pages}
                  className="w-8 px-0"
                  onClick={() => loadInvoices(pagination.page + 1)}
                />
              </div>
            </div>
          ) : null}
        </Card>

        <div className="space-y-4">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-[4px] bg-[#0176D3]/10 border border-[#0176D3]/15 flex items-center justify-center text-[#0176D3]">
                <CreditCard size={18} />
              </div>
              <div>
                <div className="text-[11px] font-bold text-[#706E6B] uppercase ">Billing Summary</div>
                <div className="text-[16px] font-bold text-[#080707]">Current Page Totals</div>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Total Amount</span>
                <span className="text-[16px] font-bold text-[#080707]">{formatCurrency(totalAmount, companyCurrency)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Paid Invoices</span>
                <span className="text-[14px] font-bold text-[#080707]">{paidCount}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#706E6B]">Pending Invoices</span>
                <span className="text-[14px] font-bold text-[#080707]">{unpaidCount}</span>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-[4px] bg-[#0176D3]/10 border border-[#0176D3]/15 flex items-center justify-center text-[#0176D3]">
                <Globe size={18} />
              </div>
              <div>
                <div className="text-[11px] font-bold text-[#706E6B] uppercase ">Status</div>
                <div className="text-[16px] font-bold text-[#080707]">Ledger Sync</div>
              </div>
            </div>
            <div className="space-y-3">
              <Badge variant={loading ? "amber" : "emerald"}>
                {loading ? "Syncing" : "Ready"}
              </Badge>
              <p className="text-[12px] font-medium text-[#706E6B] leading-relaxed mb-0">
                Invoice records are available for download as PDF files from this workspace.
              </p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-[4px] bg-[#0176D3]/10 border border-[#0176D3]/15 flex items-center justify-center text-[#0176D3]">
                <Calendar size={18} />
              </div>
              <div>
                <div className="text-[11px] font-bold text-[#706E6B] uppercase ">Last Updated</div>
                <div className="text-[16px] font-bold text-[#080707]">
                  {new Date().toLocaleTimeString("en-IN", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
            </div>
            <p className="text-[12px] font-medium text-[#706E6B] mb-0">
              Use the download action to retrieve invoice PDFs for finance or compliance records.
            </p>
          </Card>
        </div>
      </div>
    </DashboardModuleShell>
  );
}
